import React, { useEffect, useMemo, useState } from 'react';
import {
  AlignVerticalJustifyEnd,
  Box,
  Copy,
  Grid3X3,
  Maximize2,
  Move3D,
  Palette,
  Rotate3D,
  Ruler,
  Scale3D,
  Trash2
} from 'lucide-react';
import MaterialEditor from './MaterialEditor';

const AXES = ['x', 'y', 'z'];

const fmt = (value, digits = 2) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '0';
  return Number(number.toFixed(digits)).toString();
};

const toVectorState = (vector, digits = 2) => ({
  x: fmt(vector?.x ?? 0, digits),
  y: fmt(vector?.y ?? 0, digits),
  z: fmt(vector?.z ?? 0, digits)
});

const parseVector = (vector) => ({
  x: Number(vector.x),
  y: Number(vector.y),
  z: Number(vector.z)
});

function NumberField({ label, value, onChange, step = '0.1', suffix }) {
  return (
    <label className="field">
      <span>{label}</span>
      <div className="field-input">
        <input
          type="number"
          step={step}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
        {suffix && <em>{suffix}</em>}
      </div>
    </label>
  );
}

function ToggleRow({ label, checked, onChange }) {
  return (
    <label className="toggle-row">
      <span>{label}</span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
    </label>
  );
}

export default function InspectorPanel({
  selectedInfo,
  selectedObject,
  modelGroup,
  stats,
  transformMode,
  showGrid,
  showAxes,
  wireframe,
  showBuildPlate,
  buildPlateSize,
  onTransformModeChange,
  onToggleGrid,
  onToggleAxes,
  onToggleWireframe,
  onToggleBuildPlate,
  onBuildPlateSizeChange,
  onApplyTransform,
  onApplyDimensions,
  onPlaceOnPlatform,
  onDuplicateSelected,
  onDeleteSelected,
  onFrameSelection,
  onMaterialUpdate
}) {
  const [dimensions, setDimensions] = useState(toVectorState());
  const [position, setPosition] = useState(toVectorState());
  const [rotation, setRotation] = useState(toVectorState());
  const [scale, setScale] = useState(toVectorState({ x: 1, y: 1, z: 1 }, 3));
  const [lockAspect, setLockAspect] = useState(true);

  useEffect(() => {
    if (!selectedInfo) return;
    setDimensions(toVectorState(selectedInfo.dimensions));
    setPosition(toVectorState(selectedInfo.position));
    setRotation(toVectorState(selectedInfo.rotation));
    setScale(toVectorState(selectedInfo.scale, 3));
  }, [selectedInfo]);

  const sceneStats = useMemo(() => {
    const size = stats?.size || {};
    return [
      { label: 'Triangles', value: (stats?.triangles || 0).toLocaleString() },
      { label: 'Vertices', value: (stats?.vertices || 0).toLocaleString() },
      { label: 'Width', value: `${fmt(size.x)} mm` },
      { label: 'Height', value: `${fmt(size.y)} mm` },
      { label: 'Depth', value: `${fmt(size.z)} mm` }
    ];
  }, [stats]);

  const setVectorValue = (setter, axis, value) => {
    setter((current) => ({ ...current, [axis]: value }));
  };

  const applyTransform = () => {
    onApplyTransform({
      position: parseVector(position),
      rotation: parseVector(rotation),
      scale: parseVector(scale)
    });
  };

  const applyDimensions = () => {
    onApplyDimensions(parseVector(dimensions), lockAspect);
  };

  return (
    <aside className="inspector-panel">
      <div className="panel-section">
        <div className="section-title">
          <Box size={16} />
          <span>Selection</span>
        </div>

        {selectedInfo ? (
          <div className="selection-card">
            <div>
              <strong>{selectedInfo.name}</strong>
              <span>{selectedInfo.type}</span>
            </div>
            <button
              className="icon-btn with-tooltip"
              onClick={onFrameSelection}
              data-tooltip="Frame the selected object in the viewport"
              aria-label="Frame selected object"
            >
              <Maximize2 size={16} />
            </button>
          </div>
        ) : (
          <div className="empty-state-compact">No object selected</div>
        )}

        <div className="command-row">
          <button
            className="btn btn-secondary with-tooltip"
            onClick={onDuplicateSelected}
            disabled={!selectedInfo}
            data-tooltip={selectedInfo ? 'Create a copy of the selected object' : 'Select an object to duplicate it'}
          >
            <Copy size={15} />
            Duplicate
          </button>
          <button
            className="btn btn-danger with-tooltip"
            onClick={onDeleteSelected}
            disabled={!selectedInfo}
            data-tooltip={selectedInfo ? 'Remove the selected object from the scene' : 'Select an object to delete it'}
          >
            <Trash2 size={15} />
            Delete
          </button>
        </div>
      </div>

      <div className="panel-section">
        <div className="section-title">
          <Ruler size={16} />
          <span>Dimensions</span>
        </div>

        <div className="axis-grid">
          {AXES.map((axis) => (
            <NumberField
              key={axis}
              label={axis === 'x' ? 'W' : axis === 'y' ? 'H' : 'D'}
              value={dimensions[axis]}
              onChange={(value) => setVectorValue(setDimensions, axis, value)}
              suffix="mm"
            />
          ))}
        </div>

        <ToggleRow label="Lock ratio" checked={lockAspect} onChange={setLockAspect} />

        <button
          className="btn btn-primary w-full with-tooltip"
          onClick={applyDimensions}
          disabled={!selectedInfo}
          data-tooltip={selectedInfo ? 'Apply the width, height, and depth values to the selected model' : 'Select an object to resize it'}
        >
          <Scale3D size={16} />
          Resize Model
        </button>
      </div>

      <div className="panel-section">
        <div className="section-title">
          <Move3D size={16} />
          <span>Transform</span>
        </div>

        <div className="segmented-control">
          {[
            { id: 'translate', label: 'Move', icon: Move3D },
            { id: 'rotate', label: 'Rotate', icon: Rotate3D },
            { id: 'scale', label: 'Scale', icon: Scale3D }
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              className={`with-tooltip ${transformMode === id ? 'active' : ''}`}
              onClick={() => onTransformModeChange(id)}
              data-tooltip={
                id === 'translate'
                  ? 'Show move handles for the selected object'
                  : id === 'rotate'
                    ? 'Show rotation handles for the selected object'
                    : 'Show scale handles for the selected object'
              }
            >
              <Icon size={15} />
              <span>{label}</span>
            </button>
          ))}
        </div>

        <div className="inspector-subgroup">
          <span className="subgroup-label">Position</span>
          <div className="axis-grid">
            {AXES.map((axis) => (
              <NumberField
                key={axis}
                label={axis.toUpperCase()}
                value={position[axis]}
                onChange={(value) => setVectorValue(setPosition, axis, value)}
                suffix="mm"
              />
            ))}
          </div>
        </div>

        <div className="inspector-subgroup">
          <span className="subgroup-label">Rotation</span>
          <div className="axis-grid">
            {AXES.map((axis) => (
              <NumberField
                key={axis}
                label={axis.toUpperCase()}
                value={rotation[axis]}
                onChange={(value) => setVectorValue(setRotation, axis, value)}
                step="1"
                suffix="deg"
              />
            ))}
          </div>
        </div>

        <div className="inspector-subgroup">
          <span className="subgroup-label">Scale</span>
          <div className="axis-grid">
            {AXES.map((axis) => (
              <NumberField
                key={axis}
                label={axis.toUpperCase()}
                value={scale[axis]}
                onChange={(value) => setVectorValue(setScale, axis, value)}
                step="0.01"
              />
            ))}
          </div>
        </div>

        <div className="command-row">
          <button
            className="btn btn-secondary with-tooltip"
            onClick={onPlaceOnPlatform}
            disabled={!selectedInfo}
            data-tooltip={selectedInfo ? 'Drop the selected object onto the workplane' : 'Select an object to place it on the workplane'}
          >
            <AlignVerticalJustifyEnd size={15} />
            Ground
          </button>
          <button
            className="btn btn-primary with-tooltip"
            onClick={applyTransform}
            disabled={!selectedInfo}
            data-tooltip={selectedInfo ? 'Apply the typed position, rotation, and scale values' : 'Select an object to apply transform values'}
          >
            Apply
          </button>
        </div>
      </div>

      <div className="panel-section">
        <div className="section-title">
          <Palette size={16} />
          <span>Material</span>
        </div>
        <MaterialEditor
          targetObject={selectedObject}
          modelGroup={modelGroup}
          disabled={!selectedObject && !modelGroup}
          compact
          onUpdate={onMaterialUpdate}
        />
      </div>

      <div className="panel-section">
        <div className="section-title">
          <Grid3X3 size={16} />
          <span>Scene</span>
        </div>

        <div className="scene-options">
          <ToggleRow label="Build plate" checked={showBuildPlate} onChange={onToggleBuildPlate} />
          <ToggleRow label="Grid" checked={showGrid} onChange={onToggleGrid} />
          <ToggleRow label="Axes" checked={showAxes} onChange={onToggleAxes} />
          <ToggleRow label="Wireframe" checked={wireframe} onChange={onToggleWireframe} />
        </div>

        <label className="field full">
          <span>Plate</span>
          <select
            value={buildPlateSize}
            onChange={(event) => onBuildPlateSizeChange(Number(event.target.value))}
          >
            <option value={150}>150 x 150 mm</option>
            <option value={200}>200 x 200 mm</option>
            <option value={220}>220 x 220 mm</option>
            <option value={250}>250 x 250 mm</option>
            <option value={300}>300 x 300 mm</option>
          </select>
        </label>

        <div className="stat-grid">
          {sceneStats.map((item) => (
            <div key={item.label}>
              <span>{item.label}</span>
              <strong>{item.value}</strong>
            </div>
          ))}
        </div>
      </div>
    </aside>
  );
}
